/**
 * ChassisList — listado de chasis multinodo (estándar de listados del CMDB).
 *
 * Un chasis multinodo es una caja que aloja varios servidores (nodos) que
 * comparten fuentes, ventiladores y backplane. Los nodos son servidores
 * normales con `chassis_id` + `chassis_node`.
 *
 * Andamiaje común (`common/ListPage.jsx`): buscador, filtros, exportar,
 * columnas, clic en fila → ficha EN LECTURA, botones
 * `[acciones propias] · Copiar · Editar · Eliminar` y diálogos comunes.
 *
 * Lo propio de esta pantalla:
 *  · Vistas: **Lista** (por defecto), **Fichas** y **Nodos** (los servidores
 *    montados en cada chasis, con los slots que quedan libres).
 *  · Filtros: Tipo, Ubicación, Rack, Estado, Ocupación de nodos y Altura (U).
 *  · Acciones propias: **Ver nodos**, **Componentes compartidos** y **Ver rack**.
 *
 * La vista de Nodos se construye con la lista de SERVIDORES (una sola consulta,
 * agrupada por `chassis_id`) en vez de pedir los nodos chasis a chasis.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ServerStackIcon,
  Square3Stack3DIcon,
  RectangleStackIcon,
  QueueListIcon,
  Squares2X2Icon,
  ListBulletIcon,
} from '@heroicons/react/24/outline';
import ListPage from './common/ListPage';
import BulkPlacementAction from './common/BulkPlacementAction';
import BulkEditAction from './common/BulkEditAction';
import Dialog from './common/Dialog';
import CloneDialog from './common/CloneDialog';
import ChassisForm from './ChassisForm';
import { useListView } from '../views/ViewsContext';
import { useAutoOpen } from '../hooks/useAutoOpen';
import useNavOrder from '../hooks/useNavOrder';
import api from '../services/api';
import useLocationPaths from '../hooks/useLocationPaths';
import { locationPathOptions } from '../utils/locationTree';
import { getServerFormStyle } from '../utils/serverFormStyle';

const STATUS_LABEL = { active: 'Activo', maintenance: 'Mantenimiento', inactive: 'Inactivo' };
const STATUS_BADGE = {
  active: 'list-badge--on',
  maintenance: 'list-badge--warn',
  inactive: 'list-badge--off',
};

const TYPE_EMOJI = {
  SuperServer: '🖥️',
  'Twin (2 nodes)': '🔁',
  'Twin² (4 nodes)': '🔁',
  BigTwin: '🔁',
  MicroCloud: '☁️',
  Blade: '🗃️',
  Modular: '🧱',
};

const typeEmoji = (type) => TYPE_EMOJI[type] || '🖥️';

/** Barra de ocupación de nodos (con los colores del tema). */
function NodeBar({ filled, total }) {
  const pct = total > 0 ? Math.round((filled / total) * 100) : 0;
  const tone = pct >= 90 ? 'list-badge--warn' : 'list-badge--on';
  const color = pct >= 90
    ? 'var(--amber-600)'
    : pct >= 60 ? 'var(--amber-600)' : 'var(--green-600)';
  return (
    <span className="chassis-node-bar" title={`${filled} de ${total} slots ocupados`}>
      <span className="chassis-node-bar-track">
        <span className="chassis-node-bar-fill" style={{ width: `${pct}%`, background: color }} />
      </span>
      <span className={`list-badge ${tone}`} style={{ marginLeft: 6 }}>{filled}/{total}</span>
    </span>
  );
}

export default function ChassisList({ companyId }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);

  const [items, setItems] = useState([]);
  const { locations: todasUbicaciones, pathFor } = useLocationPaths(companyId);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bulkResetKey, setBulkResetKey] = useState(0);
  const [hlId, setHlId] = useState(null);   // resaltado por ?destacar=<id>

  // Nodos (servidores) para la vista «Nodos» y para saber los slots libres.
  const [servers, setServers] = useState([]);
  const [serversLoaded, setServersLoaded] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState('view');
  const [selectedChassis, setSelectedChassis] = useState(null);

  const [chassisToCopy, setChassisToCopy] = useState(null);
  const [chassisToView, setChassisToView] = useState(null);   // nodos
  const [viewNodes, setViewNodes] = useState(null);
  const [chassisToComponents, setChassisToComponents] = useState(null);
  const [components, setComponents] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState('');

  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterRack, setFilterRack] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterOccupancy, setFilterOccupancy] = useState('');
  const [filterHeight, setFilterHeight] = useState('');

  const fetchChassis = async () => {
    if (!companyId) {
      setItems([]);
      setError(tr('msg.select_company', 'Selecciona una empresa para ver chasis.'));
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const r = await api.getChassis({ company_id: companyId });
      setItems(Array.isArray(r.data) ? r.data : []);
      setError('');
    } catch (e) {
      setError(e.message || tr('chassis.load_error', 'Error cargando chasis'));
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChassis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  /* ── Apertura de la ficha (lectura por defecto) ─────────────────────── */
  const handleOpen = (c) => { setSelectedChassis(c); setFormMode('view'); setShowForm(true); };
  const handleEdit = (c) => { setSelectedChassis(c); setFormMode('edit'); setShowForm(true); };
  const handleCreate = () => { setSelectedChassis(null); setFormMode('edit'); setShowForm(true); };
  useAutoOpen(items, handleOpen);

  /**
   * Enlace directo desde otra ficha (p. ej. el nodo de un chasis).
   *
   *   ?destacar=<id>            resalta y centra la fila (convención del CMDB)
   *   ?destacar=<id>&abrir=1    abre la ficha directamente (ficha → ficha)
   */
  useEffect(() => {
    const raw = searchParams.get('destacar');
    if (!raw || !items.length) return undefined;
    const id = Number(raw);
    const target = items.find((c) => c.id === id);
    if (!target) return undefined;
    const abrir = searchParams.get('abrir') === '1';
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('destacar');
      next.delete('abrir');
      return next;
    }, { replace: true });
    if (abrir) { handleOpen(target); return undefined; }
    setHlId(id);
    const row = document.getElementById(`chassis-row-${id}`);
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHlId(null), 4000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, items, setSearchParams]);

  const handleFormClose = () => {
    setShowForm(false);
    setSelectedChassis(null);
    fetchChassis();
  };

  /* ── Borrado (desvincula los nodos, no los borra) ───────────────────── */
  const handleDelete = async (c) => {
    try {
      await api.deleteChassis(c.id, companyId);
      await fetchChassis();
    } catch (e) {
      setError(e.message || tr('chassis.delete_error', 'No se pudo eliminar el chasis'));
    }
  };

  /* ── Copiar ─────────────────────────────────────────────────────────── */
  const handleCopy = async (name) => {
    setBusy(true);
    setDialogError('');
    try {
      await api.cloneChassis(chassisToCopy.id, { name }, companyId);
      setChassisToCopy(null);
      await fetchChassis();
    } catch (e) {
      setDialogError(e.message || tr('chassis.copy_error', 'No se pudo copiar el chasis'));
    } finally {
      setBusy(false);
    }
  };

  /* ── Ver nodos y componentes compartidos ────────────────────────────── */
  const openNodes = async (c) => {
    setChassisToView(c);
    setViewNodes(null);
    setDialogError('');
    try {
      const r = await api.getChassisNodes(c.id, companyId);
      setViewNodes(r.data || []);
    } catch (e) {
      setDialogError(e.message || tr('chassis.nodes_error', 'No se pudieron cargar los nodos'));
    }
  };

  const openComponents = async (c) => {
    setChassisToComponents(c);
    setComponents(null);
    setDialogError('');
    try {
      const r = await api.getChassisComponents(c.id, companyId);
      setComponents(r.data || []);
    } catch (e) {
      setDialogError(e.message || tr('chassis.components_error', 'No se pudieron cargar los componentes'));
    }
  };

  /* ── Datos derivados ────────────────────────────────────────────────── */
  const nodesByChassis = useMemo(() => {
    const map = new Map();
    servers.forEach((s) => {
      if (s.chassis_id == null) return;
      if (!map.has(s.chassis_id)) map.set(s.chassis_id, []);
      map.get(s.chassis_id).push(s);
    });
    map.forEach((list) => list.sort((a, b) => {
      const na = a.chassis_node == null ? 999 : Number(a.chassis_node);
      const nb = b.chassis_node == null ? 999 : Number(b.chassis_node);
      return na - nb || (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' });
    }));
    return map;
  }, [servers]);

  const isFull = (c) => (c.total_node_slots || 0) > 0
    && (c.node_count || 0) >= (c.total_node_slots || 0);

  const optionsFrom = (values) => [...new Set(values.filter(Boolean))]
    .sort((a, b) => String(a).localeCompare(String(b), 'es', { sensitivity: 'base' }))
    .map((v) => ({ value: String(v), label: String(v) }));

  const typeOptions = useMemo(() => optionsFrom(items.map((c) => c.chassis_type)), [items]);
  // El filtro trabaja con el id de la ubicación; la etiqueta es la ruta completa.
  const locationOptions = useMemo(
    () => locationPathOptions(todasUbicaciones, {
      onlyIds: items.map((c) => c.location_id).filter(Boolean),
    }),
    [items, todasUbicaciones],
  );
  const rackOptions = useMemo(
    () => [...new Map(items.filter((c) => c.rack_id && c.rack_name)
      .map((c) => [String(c.rack_id), c.rack_name])).entries()]
      .sort((a, b) => a[1].localeCompare(b[1], 'es', { sensitivity: 'base' }))
      .map(([value, label]) => ({ value, label })),
    [items],
  );
  const heightOptions = useMemo(
    () => [...new Set(items.map((c) => c.size_u).filter(Boolean))].sort((a, b) => a - b)
      .map((h) => ({ value: String(h), label: `${h} U` })),
    [items],
  );

  const filteredItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items.filter((c) => {
      if (term) {
        const haystack = [c.name, c.manufacturer, c.model, c.serial_number, c.chassis_type,
          c.location_name, c.rack_name].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      if (filterType && c.chassis_type !== filterType) return false;
      if (filterLocation && String(c.location_id ?? '') !== filterLocation) return false;
      if (filterRack && String(c.rack_id ?? '') !== filterRack) return false;
      if (filterStatus && (c.status || '') !== filterStatus) return false;
      if (filterOccupancy === 'free' && isFull(c)) return false;
      if (filterOccupancy === 'full' && !isFull(c)) return false;
      if (filterHeight && String(c.size_u ?? '') !== filterHeight) return false;
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, search, filterType, filterLocation, filterRack, filterStatus, filterOccupancy, filterHeight]);

  // Orden de las fichas congelado mientras la ficha está abierta.
  const navItems = useNavOrder(filteredItems, selectedChassis, showForm);

  /** Carga perezosa de servidores: solo hace falta para la vista «Nodos». */
  const { view: activeChassisView } = useListView('chassis', 'list');
  useEffect(() => {
    if (activeChassisView !== 'nodes' || serversLoaded || !companyId) return;
    let cancelled = false;
    api.getServers(companyId)
      .then((r) => { if (!cancelled) { setServers(r.data || []); setServersLoaded(true); } })
      .catch(() => { if (!cancelled) setServersLoaded(true); });
    return () => { cancelled = true; };
  }, [activeChassisView, serversLoaded, companyId]);

  const exportRows = useMemo(() => filteredItems.map((c) => ({
    Nombre: c.name || '',
    Tipo: c.chassis_type || '',
    Fabricante: c.manufacturer || '',
    Modelo: c.model || '',
    'Nodos ocupados': c.node_count ?? 0,
    'Slots totales': c.total_node_slots ?? '',
    'Slots libres': Math.max(0, (c.total_node_slots || 0) - (c.node_count || 0)),
    Ubicación: pathFor(c.location_id) || c.location_name || '',
    Rack: c.rack_name || '',
    'Altura (U)': c.size_u ?? '',
    Posición: c.position_u ?? '',
    PSUs: c.psu_count ? `${c.psu_count}${c.psu_wattage ? ` × ${c.psu_wattage} W` : ''}${c.psu_redundant ? ' (redundantes)' : ''}` : '',
    'Nº de serie': c.serial_number || '',
    Estado: STATUS_LABEL[c.status] || c.status || '',
  })), [filteredItems, pathFor]);

  /* ── Columnas ───────────────────────────────────────────────────────── */
  const columns = useMemo(() => [
    {
      key: 'name',
      label: tr('chassis.name', 'Nombre'),
      sortable: true,
      sortValue: (c) => c.name,
      render: (c) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span aria-hidden="true">{typeEmoji(c.chassis_type)}</span>
          {c.name}
        </span>
      ),
    },
    {
      key: 'chassis_type',
      label: tr('chassis.type', 'Tipo'),
      sortable: true,
      render: (c) => c.chassis_type || <span className="list-hint">—</span>,
    },
    {
      key: 'manufacturer',
      label: tr('chassis.manufacturer', 'Fabricante'),
      sortable: true,
      render: (c) => c.manufacturer || <span className="list-hint">—</span>,
    },
    {
      key: 'model',
      label: tr('chassis.model', 'Modelo'),
      render: (c) => c.model || <span className="list-hint">—</span>,
    },
    {
      key: 'nodes',
      label: tr('chassis.nodes', 'Nodos'),
      sortable: true,
      sortValue: (c) => c.node_count || 0,
      render: (c) => <NodeBar filled={c.node_count || 0} total={c.total_node_slots || 1} />,
    },
    {
      key: 'location_name',
      label: tr('chassis.location', 'Ubicación'),
      sortable: true,
      sortValue: (c) => pathFor(c.location_id) || c.location_name || '',
      render: (c) => (c.location_name || c.rack_name
        ? <span>{[pathFor(c.location_id) || c.location_name, c.rack_name].filter(Boolean).join(' › ')}</span>
        : <span className="list-hint">—</span>),
    },
    {
      key: 'size_u',
      label: tr('chassis.height', 'Altura (U)'),
      sortable: true,
      sortValue: (c) => c.size_u || 0,
      render: (c) => (c.size_u ? `${c.size_u}U` : <span className="list-hint">—</span>),
    },
    {
      key: 'psu',
      label: tr('chassis.psu', 'PSUs'),
      render: (c) => (c.psu_count
        ? (
          <span title={c.psu_redundant ? tr('chassis.psu_redundant', 'Redundantes') : undefined}>
            {c.psu_count}{c.psu_wattage ? ` × ${c.psu_wattage} W` : ''}
            {c.psu_redundant && <span className="list-badge list-badge--on" style={{ marginLeft: 6 }}>N+1</span>}
          </span>
        )
        : <span className="list-hint">—</span>),
    },
    {
      key: 'status',
      label: tr('chassis.status', 'Estado'),
      sortable: true,
      sortValue: (c) => c.status || '',
      render: (c) => (
        <span className={`list-badge ${STATUS_BADGE[c.status] || 'list-badge--off'}`}>
          {tr(`chassis.status_${c.status || 'active'}`, STATUS_LABEL[c.status] || c.status)}
        </span>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, tr, pathFor]);

  /* ── Filtros ────────────────────────────────────────────────────────── */
  const filters = [
    {
      key: 'type', label: tr('chassis.filter_type', 'Tipo'),
      allLabel: tr('chassis.filter_all_types', 'Todos los tipos'),
      value: filterType, onChange: setFilterType, options: typeOptions,
    },
    {
      key: 'location', label: tr('chassis.location', 'Ubicación'),
      allLabel: tr('chassis.filter_all_locations', 'Todas las ubicaciones'),
      value: filterLocation, onChange: setFilterLocation, options: locationOptions,
    },
    {
      key: 'rack', label: tr('chassis.rack', 'Rack'),
      allLabel: tr('chassis.filter_all_racks', 'Todos los racks'),
      value: filterRack, onChange: setFilterRack, options: rackOptions,
    },
    {
      key: 'status', label: tr('chassis.status', 'Estado'),
      allLabel: tr('chassis.filter_all_states', 'Todos los estados'),
      value: filterStatus, onChange: setFilterStatus,
      options: [
        { value: 'active', label: tr('chassis.status_active', 'Activo') },
        { value: 'maintenance', label: tr('chassis.status_maintenance', 'Mantenimiento') },
        { value: 'inactive', label: tr('chassis.status_inactive', 'Inactivo') },
      ],
    },
    {
      key: 'occupancy', label: tr('chassis.filter_occupancy', 'Ocupación'),
      allLabel: tr('chassis.filter_all_occupancy', 'Cualquier ocupación'),
      value: filterOccupancy, onChange: setFilterOccupancy,
      options: [
        { value: 'free', label: tr('chassis.filter_with_free', 'Con slots libres') },
        { value: 'full', label: tr('chassis.filter_full', 'Completos') },
      ],
    },
    {
      key: 'height', label: tr('chassis.filter_height', 'Altura'),
      allLabel: tr('chassis.filter_all_heights', 'Cualquier altura'),
      value: filterHeight, onChange: setFilterHeight, options: heightOptions,
    },
  ];

  /* ── Vistas ─────────────────────────────────────────────────────────── */
  const CHASSIS_VIEWS = useMemo(() => [
    { key: 'list', label: tr('chassis.view_list', 'Lista'), icon: ListBulletIcon, title: tr('chassis.view_list_hint', 'Tabla con todas las columnas') },
    { key: 'card', label: tr('chassis.view_cards', 'Fichas'), icon: Squares2X2Icon, title: tr('chassis.view_cards_hint', 'Tarjetas con la ocupación de nodos'), render: renderChassisCards },
    { key: 'nodes', label: tr('chassis.view_nodes', 'Nodos'), icon: QueueListIcon, title: tr('chassis.view_nodes_hint', 'Los servidores montados en cada chasis y los slots libres'), render: renderChassisNodesView },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, servers, nodesByChassis, serversLoaded]);

  const freeSlots = (c) => Math.max(0, (c.total_node_slots || 0) - (c.node_count || 0));
  const fullCount = items.filter(isFull).length;

  const subtitle = `${items.length} ${tr('chassis.plural', 'chasis')}`
    + (fullCount ? ` · ${fullCount} ${tr('chassis.full_plural', 'completos')}` : '')
    + ` · ${filteredItems.length} ${tr('location.filtered', 'filtrados')}`;

  /** Vista de fichas. */
  function renderChassisCards({ rows, rowActions }) {
    return (
      <div className="list-cards">
        {rows.map((c) => (
          <div
            key={c.id}
            className="list-card"
            role="button"
            tabIndex={0}
            onClick={() => handleOpen(c)}
            onKeyDown={(event) => { if (event.key === 'Enter') handleOpen(c); }}
            title={`${tr('action.view', 'Ver')} ${c.name}`}
          >
            <div className="list-card-head">
              <div>
                <div className="list-card-title">
                  <span aria-hidden="true">{typeEmoji(c.chassis_type)}</span> {c.name}
                </div>
                {(c.manufacturer || c.model) && (
                  <div className="list-hint">{[c.manufacturer, c.model].filter(Boolean).join(' · ')}</div>
                )}
              </div>
              <span className={`list-badge ${STATUS_BADGE[c.status] || 'list-badge--off'}`}>
                {tr(`chassis.status_${c.status || 'active'}`, STATUS_LABEL[c.status] || c.status)}
              </span>
            </div>

            {c.chassis_type && <div className="list-card-meta"><span>{c.chassis_type}</span></div>}

            <NodeBar filled={c.node_count || 0} total={c.total_node_slots || 1} />

            <div className="list-card-meta">
              {(c.location_name || c.rack_name) && (
                <span>📍 {[pathFor(c.location_id) || c.location_name, c.rack_name].filter(Boolean).join(' › ')}</span>
              )}
              {c.size_u && <span>{c.size_u}U</span>}
              {c.psu_count && <span>🔌 {c.psu_count} PSU</span>}
            </div>

            <div className="list-card-actions">{rowActions(c)}</div>
          </div>
        ))}
      </div>
    );
  }

  /**
   * Vista de Nodos: por cada chasis, los servidores que lleva montados y los
   * slots que quedan libres. Cada nodo lleva a su ficha en Servidores
   * (`?destacar=`, la convención de todos los módulos).
   */
  function renderChassisNodesView({ rows }) {
    if (!serversLoaded) {
      return <p className="list-hint">{tr('chassis.loading_nodes', 'Cargando nodos…')}</p>;
    }
    return (
      <div className="list-cards">
        {rows.map((c) => {
          const nodes = nodesByChassis.get(c.id) || [];
          const free = freeSlots(c);
          return (
            <div key={c.id} className="list-card" style={{ cursor: 'default' }}>
              <div className="list-card-head">
                <div>
                  <div className="list-card-title">
                    <span aria-hidden="true">{typeEmoji(c.chassis_type)}</span> {c.name}
                  </div>
                  <div className="list-hint">
                    {c.location_name || c.rack_name
                      ? [pathFor(c.location_id) || c.location_name, c.rack_name].filter(Boolean).join(' › ')
                      : tr('chassis.no_location', 'Sin ubicación')}
                  </div>
                </div>
                <span className="list-badge list-badge--on">
                  {nodes.length}/{c.total_node_slots || 0}
                </span>
              </div>

              <div className="list-subrows">
                {nodes.map((node) => (
                  <button
                    key={node.id}
                    type="button"
                    className="list-subrow"
                onClick={() => navigate(`/infrastructure/servers?destacar=${node.id}&abrir=1`)}
                    title={tr('chassis.open_node', 'Abrir la ficha del servidor')}
                  >
                    <span className="list-code">
                      {node.chassis_node != null
                        ? `${tr('chassis.node', 'Nodo')} ${node.chassis_node}`
                        : tr('chassis.no_slot', 'Sin slot')}
                    </span>
                    <span className="list-subrow-name">{node.name || `#${node.id}`}</span>
                    {node.ip_address && <span className="list-hint">{node.ip_address}</span>}
                  </button>
                ))}
                {nodes.length === 0 && (
                  <div className="list-hint">{tr('chassis.no_nodes', 'Sin nodos montados.')}</div>
                )}
                {free > 0 && (
                  <div className="list-hint">
                    {free} {tr('chassis.slots_free', 'slots libres')}
                  </div>
                )}
              </div>

              <div className="list-card-actions">
                <button type="button" className="list-btn-secondary" onClick={() => openNodes(c)}>
                  {tr('chassis.view_nodes', 'Ver nodos')}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <>
      {showForm && (
        <ChassisForm
          chassis={selectedChassis}
          companyId={companyId}
          initialMode={formMode}
          variant={getServerFormStyle() === 'cards' ? 'cards' : 'classic'}
          navItems={navItems}
          navIndex={selectedChassis ? navItems.findIndex((c) => String(c.id) === String(selectedChassis.id)) : -1}
          onNavigate={(destino) => setSelectedChassis(destino)}
          onClose={handleFormClose}
          onSave={(creado) => {
            fetchChassis();
            // Alta: el listado adopta el registro creado y la ficha sigue abierta.
            if (creado?.id) { setSelectedChassis(creado); setFormMode('edit'); }
          }}
        />
      )}

      <div style={{ display: showForm ? 'none' : undefined }}>
        <ListPage
          icon={ServerStackIcon}
          title={tr('chassis.title', 'Chasis multinodo')}
          subtitle={subtitle}
          onCreate={handleCreate}
          createLabel={tr('chassis.new', 'Nuevo chasis')}
          createDisabled={!companyId}

          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={tr('chassis.search_placeholder', 'Buscar por nombre, fabricante, modelo, serie…')}
          filters={filters}
          exportRows={exportRows}
          exportFileBase="chasis"
          exportSheetName="Chasis"

          columns={columns}
          columnStorageKey="cmdb_cols_chassis"
          companyId={companyId}
          rows={filteredItems}
          rowKey={(c) => c.id}
          rowId={(c) => `chassis-row-${c.id}`}
          rowClassName={(c) => (c.id === hlId ? 'list-row-highlight' : '')}
          onRowClick={handleOpen}
          rowTitle={(c) => `${tr('action.view', 'Ver')} ${c.name}`}

          selectable
          selectionResetKey={bulkResetKey}
          bulkActions={({ selectedRows }) => (
            <>
              <BulkPlacementAction
                companyId={companyId}
                entity="chassis"
                supportsRack
                selectedRows={selectedRows}
                onApplied={() => {
                  setBulkResetKey((key) => key + 1);
                  fetchChassis();
                }}
              />
              <BulkEditAction
                companyId={companyId}
                entity="chassis"
                selectedRows={selectedRows}
                onApplied={() => {
                  setBulkResetKey((key) => key + 1);
                  fetchChassis();
                }}
              />
            </>
          )}

          views={CHASSIS_VIEWS}
          listKey="chassis"
          defaultView="list"

          loading={loading}
          loadingText={tr('msg.loading', 'Cargando chasis…')}
          error={error}

          empty={{
            icon: '🗄️',
            title: items.length === 0
              ? tr('chassis.empty', 'No hay chasis registrados')
              : tr('msg.no_results', 'Ningún chasis coincide con los filtros'),
            subtitle: items.length === 0 ? tr('chassis.empty_hint', 'Crea el primero para empezar.') : undefined,
            action: items.length === 0 && companyId ? (
              <button type="button" className="list-btn-primary" onClick={handleCreate}>
                ＋ {tr('chassis.new', 'Nuevo chasis')}
              </button>
            ) : undefined,
          }}

          actions={{
            extra: (c) => (
              <>
                <button
                  type="button"
                  className="list-action-btn"
                  onClick={() => openNodes(c)}
                  title={tr('chassis.view_nodes', 'Ver nodos')}
                >
                  <QueueListIcon />
                </button>
                <button
                  type="button"
                  className="list-action-btn"
                  onClick={() => openComponents(c)}
                  title={tr('chassis.view_components', 'Componentes compartidos (PSUs, ventiladores…)')}
                >
                  <Square3Stack3DIcon />
                </button>
                {c.rack_id && (
                  <button
                    type="button"
                    className="list-action-btn"
                    onClick={() => navigate(`/infrastructure/rack/${c.rack_id}`)}
                    title={tr('chassis.view_rack', 'Ver rack')}
                  >
                    <RectangleStackIcon />
                  </button>
                )}
              </>
            ),
            onCopy: (c) => { setDialogError(''); setChassisToCopy(c); },
            onEdit: handleEdit,
            onDelete: handleDelete,
            deleteTitle: tr('chassis.delete_title', 'Eliminar chasis'),
            deleteMessage: (c) => (
              <>
                {tr('chassis.delete_confirm', '¿Seguro que quieres eliminar el chasis')} <strong>{c.name}</strong>?{' '}
                {c.node_count > 0
                  ? tr('chassis.delete_with_nodes', 'Sus nodos no se borran: quedan como servidores sueltos.')
                  : tr('chassis.delete_hint', 'No se puede deshacer.')}
              </>
            ),
          }}
        />
      </div>

      {/* Diálogo: copiar chasis */}
      {chassisToCopy && (
        <CloneDialog
          title={tr('chassis.copy_title', 'Copiar chasis')}
          nameLabel={tr('chassis.copy_name', 'Nombre de la copia')}
          placeholder={tr('chassis.copy_placeholder', 'Nombre del nuevo chasis…')}
          defaultName={`${chassisToCopy.name} (copia)`}
          hint={tr('chassis.copy_hint', 'Se copiarán el tipo, los slots de nodo, la altura, las PSUs, el fabricante, el modelo, las notas y la ubicación. No se copiarán el número de serie ni la posición en el rack, y el chasis nuevo nace sin nodos ni componentes compartidos.')}
          busy={busy}
          error={dialogError}
          onClose={() => { setChassisToCopy(null); setDialogError(''); }}
          onConfirm={handleCopy}
        />
      )}

      {/* Diálogo: nodos del chasis */}
      {chassisToView && (
        <Dialog
          title={`${tr('chassis.nodes_of', 'Nodos de')} ${chassisToView.name}`}
          icon="🧩"
          maxWidth={560}
          onClose={() => { setChassisToView(null); setViewNodes(null); setDialogError(''); }}
          footer={(
            <button
              type="button"
              className="list-btn-primary"
              onClick={() => { setChassisToView(null); setViewNodes(null); }}
            >
              {tr('action.close', 'Cerrar')}
            </button>
          )}
        >
          {dialogError && <div className="alert alert-danger py-2" role="alert">{dialogError}</div>}
          {!viewNodes && !dialogError && <p className="list-hint">{tr('msg.loading', 'Cargando…')}</p>}
          {viewNodes && (
            viewNodes.length === 0
              ? <p>{tr('chassis.no_nodes', 'Sin nodos montados.')}</p>
              : (
                <div className="list-subrows">
                  {viewNodes.map((n) => (
                    <button
                      key={n.id}
                      type="button"
                      className="list-subrow"
                      onClick={() => navigate(`/infrastructure/servers?destacar=${n.id}&abrir=1`)}
                    >
                      <span className="list-code">
                        {n.chassis_node != null ? `${tr('chassis.node', 'Nodo')} ${n.chassis_node}` : tr('chassis.no_slot', 'Sin slot')}
                      </span>
                      <span className="list-subrow-name">{n.name || `#${n.id}`}</span>
                      {n.ip_address && <span className="list-hint">{n.ip_address}</span>}
                    </button>
                  ))}
                </div>
              )
          )}
        </Dialog>
      )}

      {/* Diálogo: componentes compartidos */}
      {chassisToComponents && (
        <Dialog
          title={`${tr('chassis.components_of', 'Componentes compartidos de')} ${chassisToComponents.name}`}
          icon="🔌"
          maxWidth={620}
          onClose={() => { setChassisToComponents(null); setComponents(null); setDialogError(''); }}
          footer={(
            <button
              type="button"
              className="list-btn-primary"
              onClick={() => { setChassisToComponents(null); setComponents(null); }}
            >
              {tr('action.close', 'Cerrar')}
            </button>
          )}
        >
          {dialogError && <div className="alert alert-danger py-2" role="alert">{dialogError}</div>}
          {!components && !dialogError && <p className="list-hint">{tr('msg.loading', 'Cargando…')}</p>}
          {components && (
            components.length === 0
              ? <p>{tr('chassis.no_components', 'No hay componentes compartidos registrados (usa la ficha del chasis para añadirlos).')}</p>
              : (
                <table className="list-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th>{tr('chassis.comp_type', 'Tipo')}</th>
                      <th>{tr('chassis.comp_slot', 'Slot')}</th>
                      <th>{tr('chassis.comp_model', 'Fabricante / Modelo')}</th>
                      <th>{tr('chassis.comp_serial', 'Nº de serie')}</th>
                      <th>{tr('chassis.comp_status', 'Estado')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {components.map((comp) => (
                      <tr key={comp.id}>
                        <td>{comp.component_type || '—'}</td>
                        <td>{comp.slot || '—'}</td>
                        <td>{[comp.manufacturer, comp.model].filter(Boolean).join(' · ') || '—'}</td>
                        <td>{comp.serial_number || '—'}</td>
                        <td>{comp.status || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
          )}
        </Dialog>
      )}
    </>
  );
}
